import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { dateSortComparator, EditableCellRef, EditSaveScope } from '@components/DataGrid/types';
import { applyColumnQuickFilter, caseInsensitiveIncludes } from '../columns';
import RenderHeader from '@components/DataGrid/RenderHeader';
import { HeaderSubLabel, SubColumnDef, subColumnField } from '@components/DataGrid/subColumns';
import RenderCell from '@components/DataGrid/RenderCell';
import DateSlot from '@components/DataGrid/DateSlot';
import { forwardRef, RefObject, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { useSetScheduleMutation } from '@api/Trinium/Endpoints/Leg/slice';
import { appendErrorAnnouncement } from '@/Infrastructure/Components/Announcements/AnnouncementSlice';
import { getApiErrorMessage } from '@api/getApiError';
import { getEnumValueByKey } from '@/Infrastructure/Utilities';
import DateTimeRangePicker, { DateTimePickerRangeValue } from '@forms/Components/DateTimeRangePicker';
import { GridColDefExtended } from '@mui/x-data-grid-pro';
import { apiIsoStringFormat, parseDayJs } from '@/Infrastructure/Utilities/dayjs.type';
import { EditableCell, resolveEditTargetRows } from '../types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useAppDispatch, useAppSelector } from '@reduxStore';
import { LegStatus } from '@api/Trinium/types';

import styles from '../../RenderCell.module.css';

const scheduledPickupField = 'Scheduled Pickup';
const headerDisplayName = 'Scheduled Pickup';

/** Sub-values of the Scheduled Pickup column sortable via hidden companion columns */
const scheduledPickupSubColumns: SubColumnDef<DispatchLeg>[] = [{ key: 'ref', label: 'Pickup Ref #', valueGetter: (row) => row.puRefNo }];

const scheduledPickupSubLabels: HeaderSubLabel[] = scheduledPickupSubColumns.map((sub) => ({
  label: sub.label,
  field: subColumnField(scheduledPickupField, sub.key)
}));

const scheduledPickupDefinition: GridColDefExtended<DispatchLeg> = {
  field: scheduledPickupField,
  minWidth: 75,
  width: 160,
  headerName: 'Scheduled Pickup From|||Scheduled Pickup To|||Pickup Reference #',
  headerDisplayName,
  subColumns: scheduledPickupSubColumns,
  /** Mobile cards invoke renderHeader without params (RenderCardCell), so they fall back to the
   * static secondaryText instead of mounting the grid-subscribing sort labels per card */
  renderHeader: (params) => (
    <RenderHeader
      primaryText={headerDisplayName}
      subLabels={params.field ? scheduledPickupSubLabels : undefined}
      secondaryText='Pickup Ref #'
    />
  ),
  renderCell: ({ row }) => (
    <RenderCell
      editable
      primarySlot={
        <DateSlot
          fromDate={row.scheduledPickupDateFrom}
          toDate={row.scheduledPickupDateTo}
          usePastDueStyle
        />
      }
      secondarySlot={
        row.puRefNo ? (
          <span
            className={styles['secondary-text']}
            title={`Ref No: ${row.puRefNo}`}>
            {row.puRefNo}
          </span>
        ) : undefined
      }
    />
  ),
  disableReorder: true,
  getApplyQuickFilterFn: (value: string) => {
    return (_, row): boolean =>
      applyColumnQuickFilter(value, row, (value: string, row: DispatchLeg) => {
        const { scheduledPickupDateFrom, scheduledPickupDateTo, puRefNo } = row;
        return (
          caseInsensitiveIncludes(value, [scheduledPickupDateFrom, scheduledPickupDateTo]) || caseInsensitiveIncludes(value, [puRefNo])
        );
      });
  },
  valueGetter: (_, row: DispatchLeg) =>
    `${row.scheduledPickupDateFrom?.date ?? ''} ${row.scheduledPickupDateFrom?.time ?? ''}|||${row.scheduledPickupDateTo?.date ?? ''} ${
      row.scheduledPickupDateTo?.time ?? ''
    }|||${row.puRefNo?.replaceAll(',', ' ') ?? ''}`,
  sortable: true,
  sortComparator: dateSortComparator,
  renderEditableCell: (row: DispatchLeg, ref: RefObject<EditableCellRef | null>, selectedRows?: DispatchLeg[]) => (
    <ScheduledPickupEditCell
      row={row}
      selectedRows={selectedRows}
      ref={ref}
    />
  )
};

export default scheduledPickupDefinition;

const canUpdateScheduledPickup = (row: DispatchLeg) => {
  const legStatus = getEnumValueByKey(LegStatus, row.legStatus) as LegStatus;
  return ![LegStatus.PU, LegStatus.DL].includes(legStatus);
};

const ScheduledPickupEditCell = forwardRef<EditableCellRef, EditableCell<DispatchLeg>>(({ row, selectedRows }, ref) => {
  const appDispatch = useAppDispatch();
  const { number: terminalNumber } = useCurrentTerminal();
  const [setSchedule] = useSetScheduleMutation();
  const { scheduledPickupDateFrom, scheduledPickupDateTo } = row;
  const isLoading = useAppSelector((state) => state.dispatchBoardState.loadingSet.includes(row.legId));

  const initFrom = useMemo(() => parseDayJs(scheduledPickupDateFrom?.unix), [scheduledPickupDateFrom]);
  const initTo = useMemo(() => parseDayJs(scheduledPickupDateTo?.unix), [scheduledPickupDateTo]);

  const [rangeVal, setRangeVal] = useState<DateTimePickerRangeValue>([initFrom, initTo]);

  /** The clicked leg gates the edit; a batch save extends it to the eligible selected legs */
  const canUpdate = canUpdateScheduledPickup(row);

  const saveHandler = useCallback(
    (scope: EditSaveScope) => {
      const [fromVal, toVal] = rangeVal;
      const fromChanged =
        (fromVal?.isValid() && initFrom?.isValid() && fromVal.diff(initFrom) !== 0) ||
        (initFrom?.isValid() && !fromVal) ||
        (!initFrom && fromVal?.isValid());
      const toChanged =
        (toVal?.isValid() && initTo?.isValid() && toVal.diff(initTo) !== 0) ||
        (initTo?.isValid() && !toVal) ||
        (!initTo && toVal?.isValid());

      if (fromChanged || toChanged) {
        const targetRows = resolveEditTargetRows(scope, row, selectedRows, canUpdateScheduledPickup);
        // Clearing both sides has to go through the remove flag - setSchedule rejects a request where every
        // schedule field/flag is unset, and passing schdPuTo alone as null there means "leave it as-is", not "clear it".
        const isFullClear = !fromVal?.isValid() && !toVal?.isValid();

        void setSchedule({
          terminalNumber,
          actionType: {
            data: {
              legIds: targetRows.map((targetRow) => targetRow.legId).filter((legId) => legId > 0),
              ...(isFullClear
                ? { removeSchdPuDate: true }
                : {
                    schdPuFrom: fromVal?.isValid() ? fromVal.format(apiIsoStringFormat) : null,
                    schdPuTo: toVal?.isValid() ? toVal.format(apiIsoStringFormat) : null
                  })
            }
          }
        })
          .unwrap()
          .catch((error: unknown) => {
            appDispatch(appendErrorAnnouncement({ message: getApiErrorMessage(error) }));
          });
      }
    },
    [appDispatch, terminalNumber, setSchedule, initFrom, initTo, rangeVal, row, selectedRows]
  );

  /** Define the callable Ref functions */
  useImperativeHandle(ref, () => {
    return {
      saveChanges: saveHandler
    };
  }, [saveHandler]);

  return (
    <div style={{ paddingBottom: '8px' }}>
      <DateTimeRangePicker
        value={rangeVal}
        onChange={(newValue) => {
          setRangeVal(newValue);
        }}
        disabled={isLoading || !canUpdate}
        customProps={{
          labelText: 'Scheduled Pickup',
          isDirty: false,
          onClear: () => {
            setRangeVal([null, null]);
          },
          variant: 'standard',
          disabledReason: isLoading
            ? 'Updating...'
            : !canUpdate
              ? 'Scheduled Pickup cannot be changed once the leg is picked up'
              : undefined
        }}
      />
    </div>
  );
});
